import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * User-friendly error/warning/info dialog component
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Callback when dialog is closed
 * @param {string} title - Dialog title
 * @param {string} message - Main message to display
 * @param {string} severity - 'error' | 'warning' | 'info' (default: 'error')
 * @param {string} actionLabel - Label for the action button (default: 'OK')
 * @param {function} onAction - Callback for action button (defaults to onClose)
 * @param {string} secondaryActionLabel - Optional secondary button label
 * @param {function} onSecondaryAction - Callback for secondary button
 */
const ErrorDialog = ({
  open,
  onClose,
  title,
  message,
  severity = 'error',
  actionLabel = 'OK',
  onAction,
  secondaryActionLabel,
  onSecondaryAction
}) => {
  const getIcon = () => {
    switch (severity) {
      case 'warning':
        return <WarningAmberIcon sx={{ fontSize: 48, color: 'warning.main' }} />;
      case 'info':
        return <InfoOutlinedIcon sx={{ fontSize: 48, color: 'info.main' }} />;
      default:
        return <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main' }} />;
    }
  };

  const getAlertSeverity = () => {
    return severity === 'error' ? 'error' : severity === 'warning' ? 'warning' : 'info';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {getIcon()}
          <Typography variant="h6" component="span">
            {title || (severity === 'error' ? 'Error' : severity === 'warning' ? 'Warning' : 'Information')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity={getAlertSeverity()} sx={{ mt: 1 }}>
          <Typography variant="body1">
            {message}
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {secondaryActionLabel && (
          <Button
            onClick={onSecondaryAction || onClose}
            color="inherit"
          >
            {secondaryActionLabel}
          </Button>
        )}
        <Button
          onClick={onAction || onClose}
          variant="contained"
          color={severity === 'error' ? 'error' : severity === 'warning' ? 'warning' : 'primary'}
          autoFocus
        >
          {actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErrorDialog;

